import React, { useState, useEffect } from "react";
import { Box, Text, Select, VStack, HStack, Icon } from "@chakra-ui/react";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { FaTasks, FaPlayCircle, FaCheckCircle } from "react-icons/fa";
import { IconWrapper } from "../../components/IconWrapper";

// Register chart.js components
Chart.register(...registerables);

// Define the Task interface
interface Task {
  id: string;
  name: string;
  status: "Not-started" | "In-progress" | "completed";
}

// Props for the TaskChart component
interface TaskChartProps {
  tasks: Task[];
  length: number;
}

const TaskChart: React.FC<TaskChartProps> = ({ tasks, length }) => {
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  // Categorize tasks by status
  const taskStatusCount = {
    "Not-started": 0,
    "In-progress": 0,
    completed: 0,
    Assigned: 0, // This will track assigned tasks
  };

  // Count tasks by status and assigned status
  tasks.forEach((task) => {
    if (taskStatusCount[task.status] !== undefined) {
      taskStatusCount[task.status] += 1;
    }
  });

  // Count assigned tasks
  taskStatusCount["Assigned"] = length;

  // Prepare the data for the chart
  const chartData = {
    labels: ["Assigned", "Not started", "In Progress", "Completed"],
    datasets: [
      {
        label: "Task Count",
        data: [
          taskStatusCount["Assigned"],
          taskStatusCount["Not-started"],
          taskStatusCount["In-progress"],
          taskStatusCount["completed"],
        ],
        backgroundColor: [
          "#FFB3B3", // Light red for Assigned
          "#FFE066", // Light yellow for Not started
          "#B2F2BB", // Light green for In Progress
          "#A0C4FF", // Light blue for Completed
        ],

        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,

    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          label: (tooltipItem: any) => {
            return `${tooltipItem.label}: ${tooltipItem.raw} Tasks`;
          },
        },
      },
      legend: {
        display: false, // Hide legend if not needed
      },
    },
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value);
  };

  return (
    <Box
      p={5}
      borderRadius="md"
      display={"flex"}
      height="55vh"
      width="100%"
      overflow="hidden" // Prevent scrolling in the container
    >
      <Box width={"60%"}>
        {" "}
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Chart Overview
        </Text>
        {/* Bar chart with fixed height */}
        <Box height="300px" width="100%" bg="white" borderRadius="md" p={4}>
          <Bar data={chartData} options={chartOptions} />
        </Box>
      </Box>

      <Box
        width="320px"
        height={"300px"}
        overflow={"auto"}
        bg="gray.50"
        borderRadius="lg"
        boxShadow="md"
        p={6}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={1}>
          Task details
        </Text>

        <VStack spacing={3} align="stretch" width="100%">
          <HStack
            justify="space-between"
            p={2}
            borderRadius="md"
            bg="#FFB3C9"
            color="white"
            _hover={{ bg: "#ff4444" }}
            transition="0.3s"
          >
            <IconWrapper icon={FaTasks} size={16} className="text-gray-600" />
            <Text fontWeight="semibold">Assigned</Text>
            <Text fontWeight="bold">{taskStatusCount["Assigned"]}</Text>
          </HStack>

          <HStack
            justify="space-between"
            p={2}
            borderRadius="md"
            bg="#ffcc00"
            color="white"
            _hover={{ bg: "#ff9900" }}
            transition="0.3s"
          >
            <IconWrapper
              icon={FaPlayCircle}
              size={16}
              className="text-gray-600"
            />
            <Text fontWeight="semibold">Not Started</Text>
            <Text fontWeight="bold">{taskStatusCount["Not-started"]}</Text>
          </HStack>

          <HStack
            justify="space-between"
            p={2}
            borderRadius="md"
            bg="#66ff66"
            color="white"
            _hover={{ bg: "#33cc33" }}
            transition="0.3s"
          >
            <IconWrapper
              icon={FaPlayCircle}
              size={16}
              className="text-gray-600"
            />
            <Text fontWeight="semibold">In Progress</Text>
            <Text fontWeight="bold">{taskStatusCount["In-progress"]}</Text>
          </HStack>

          <HStack
            justify="space-between"
            p={2}
            borderRadius="md"
            bg="#0099ff"
            color="white"
            _hover={{ bg: "#0073e6" }}
            transition="0.3s"
          >
            <IconWrapper
              icon={FaCheckCircle}
              size={16}
              className="text-gray-600"
            />
            <Text fontWeight="semibold">Completed</Text>
            <Text fontWeight="bold">{taskStatusCount["completed"]}</Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default TaskChart;
